import { CoreModule } from '@abp/ng.core';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Input,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbCollapseModule, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { GetNotificationInput, NotificationDto, NotificationService } from '@proxy/notification';
import { SignalRClientService } from '../shared/signalr/signalr-service';

@Component({
    selector: 'app-notification',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CoreModule, NgbDropdownModule, NgbCollapseModule],
    templateUrl: './notification.component.html',
    styleUrl: './notification.component.scss'
})
export class Notification implements OnInit {
  private readonly service = inject(NotificationService);
  private readonly destroyRef = inject(DestroyRef);
  @Input() isMobile: boolean = false;

  // Signals, not plain fields: set in a subscribe (and on every SignalR message), which an OnPush
  // ancestor does not redraw for (#42). They replace the detectChanges() workaround.
  readonly isUnread = signal(false);
  readonly unreadNoificationCount = signal(0);
  readonly isLoading = signal(false);
  readonly notificationList = signal<Array<NotificationDto>>([]);
  sorting = 'id desc';

  /**
   * `isMobile` now only selects the full-width dropdown panel (see the template). It used to also
   * apply `small-size`, which floated the bell with `position: absolute` because the element was
   * rendered outside the layout entirely; it now sits inside the mobile navbar as a normal flex
   * child, so it needs no positioning — and must not have any, or ng-bootstrap mis-anchors the
   * `container="body"` dropdown. `d-md-none` is likewise redundant: the whole navbar is mobile-only.
   */
  get className() {
    return '';
  }

  constructor(private signalRClient: SignalRClientService) {}

  ngOnInit(): void {
    this.fetchNotificationList();

    // Subscribed, not registered: every mounted bell gets the message. `takeUntilDestroyed` ties the
    // subscription to this component's lifetime, so the root-scoped service does not keep a torn-down
    // view alive.
    this.signalRClient.message$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.fetchNotificationList());
  }

  fetchNotificationList() {
    this.service.getList({sorting: this.sorting} as GetNotificationInput).subscribe({
      next: results => {
        this.isLoading.set(false);
        this.notificationList.set(results.items);
        this.isUnread.set(results.items.length > 0);
        this.unreadNoificationCount.set(results.items.length);
      },
      error: results => {
        this.isLoading.set(false);
      },
    });
  }

  clearNotification() {
    this.service.clear().subscribe({
      next: () => {
        this.fetchNotificationList();
      }
    });
  }
}
